import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../contexts/ProjectContext';
import { projectsService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { subscribeToProjectUpdates } from '../../services/realtimeService';
import { getPlanLimitsForTier } from '../../utils/planLimits';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import ProjectCard from './components/ProjectCard';
import CalendarPopup from './components/CalendarPopup';



const ProjectHistory = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { projects: localProjects, createProject, deleteProject: deleteLocalProject, renameProject, uploadedMedia, addUploadedMedia, removeUploadedMedia, uploadingMedia } = useProjects();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedToolType, setSelectedToolType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedImageModal, setSelectedImageModal] = useState(null);
  const [imagePreviewModal, setImagePreviewModal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectTool, setNewProjectTool] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isCreateToolOpen, setIsCreateToolOpen] = useState(false);
  const createToolRef = useRef(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [renamingProjectId, setRenamingProjectId] = useState(null);
  const [renamingValue, setRenamingValue] = useState('');
  // New states for calendar and storage
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeStorageTab, setActiveStorageTab] = useState(null); // 'uploaded'
  const uploadInputRef = useRef(null);
  const [mediaPage, setMediaPage] = useState(1);
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState(null);
  const [isToolTypeOpen, setIsToolTypeOpen] = useState(false);
  const toolTypeRef = useRef(null);
  const [maxProjects, setMaxProjects] = useState(null); // null = unlimited
  const [maxStorage, setMaxStorage] = useState(null); // null = unlimited
  const [storageCount, setStorageCount] = useState(0); // saved_designs + uploaded_media
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMember, setSelectedMember] = useState(''); // userId or '' = all
  const [teamMembersList, setTeamMembersList] = useState([]);
  const PROJECTS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('');

  const isTeamOwner = userProfile?.subscription_tier === 'team';

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolTypeRef.current && !toolTypeRef.current.contains(e.target)) {
        setIsToolTypeOpen(false);
      }
      if (createToolRef.current && !createToolRef.current.contains(e.target)) {
        setIsCreateToolOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // toolTypes values must match the public.tool_type enum in Supabase
  const toolTypes = [
    { value: 'moodboard-to-3d', label: 'Moodboard to 3D Room', icon: 'Layers' },
    { value: 'sketch-to-render', label: 'Sketch to Interior Render', icon: 'Pencil' },
    { value: 'combine-styles', label: 'Combine Interior Styles', icon: 'Palette' },
    { value: 'room-variation', label: 'Room Variation Generator', icon: 'Sparkles' },
    { value: 'cad-mood-room', label: 'CAD-Based Mood Room', icon: 'Box' },
    { value: 'cad-space-planner', label: 'CAD Space Planner', icon: 'LayoutGrid' },
    { value: 'furniture-editing', label: 'Furniture Editing', icon: 'Armchair' },
    { value: 'complete-room-design', label: 'Complete Room Design', icon: 'Home' },
    { value: 'site-concept', label: 'Site Concept', icon: 'Building' },
    { value: 'freestyle', label: 'Freestyle Field', icon: 'Wand2' },
  ];

  // Route by toolType (enum) — 1:1 mapping after enum expansion
  const toolRoutes = {
    'moodboard-to-3d': '/moodboard-to-3d-room',
    'sketch-to-render': '/sketch-to-interior-render',
    'combine-styles': '/combine-interior-styles',
    'room-variation': '/room-variation-generator',
    'cad-mood-room': '/cad-based-mood-room-generator',
    'cad-space-planner': '/cad-space-planner',
    'furniture-editing': '/furniture-editing',
    'complete-room-design': '/complete-room-design',
    'site-concept': '/site-concept',
    'freestyle': '/freestyle-field',
  };

  // Route by toolName (unique per tool) for project-level navigation
  const toolNameRoutes = {
    'Moodboard to 3D Room': '/moodboard-to-3d-room',
    'Sketch to Interior Render': '/sketch-to-interior-render',
    'Combine Interior Styles': '/combine-interior-styles',
    'Room Variation Generator': '/room-variation-generator',
    'Furniture Editing': '/furniture-editing',
    'Complete Room Design': '/complete-room-design',
    'CAD-Based Mood Room Generator': '/cad-based-mood-room-generator',
    'CAD Space Planner': '/cad-space-planner',
    'Site Concept': '/site-concept',
    'Freestyle Field': '/freestyle-field',
  };

  // All tools list for "Continue editing" dropdown — shows every tool regardless of toolType grouping
  const allTools = [
    { label: 'Moodboard to 3D Room', route: '/moodboard-to-3d-room' },
    { label: 'Sketch to Interior Render', route: '/sketch-to-interior-render' },
    { label: 'Combine Interior Styles', route: '/combine-interior-styles' },
    { label: 'Room Variation Generator', route: '/room-variation-generator' },
    { label: 'Furniture Editing', route: '/furniture-editing' },
    { label: 'Complete Room Design', route: '/complete-room-design' },
    { label: 'CAD-Based Mood Room Generator', route: '/cad-based-mood-room-generator' },
    { label: 'CAD Space Planner', route: '/cad-space-planner' },
    { label: 'Site Concept', route: '/site-concept' },
    { label: 'Freestyle Field', route: '/freestyle-field' },
  ];

  const handleCreateProject = async () => {
    if (!newProjectName.trim() || !newProjectTool || !user) return;
    setIsCreating(true);
    try {
      const selectedTool = toolTypes.find((t) => t.value === newProjectTool);
      const { data, error } = await projectsService.createProject(user.id, {
        name: newProjectName.trim(),
        toolType: newProjectTool,
        toolName: selectedTool?.label || newProjectTool,
        status: 'draft',
        tags: [],
        variations: 0,
      });
      if (error) {
        alert(error.message || 'Failed to create project');
        return;
      }
      if (data) {
        setProjects((prev) => [data, ...prev]);
      }
      setNewProjectName('');
      setNewProjectTool('');
      setShowCreateModal(false);
    } catch (err) {
      console.error('Create project error:', err);
      alert('Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  // Fetch plan limits + storage count
  useEffect(() => {
    if (!userProfile?.subscription_tier || !user?.id) return;
    const fetchLimitsAndStorage = async () => {
      const [limits, designsRes, uploadRes] = await Promise.all([
        getPlanLimitsForTier(userProfile.subscription_tier, user.id),
        supabase.from('saved_designs').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.rpc('count_uploaded_media', { target_user_id: user.id }),
      ]);
      setMaxProjects(limits.maxProjects);
      setMaxStorage(limits.maxStorage);
      setStorageCount((designsRes.count ?? 0) + (uploadRes.data ?? 0));
    };
    fetchLimitsAndStorage();
  }, [userProfile?.subscription_tier, user?.id]);

  // Load projects from database — reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    loadProjects(1);
  }, [user, selectedToolType, selectedStatus, selectedMember]);

  // Reload when page changes
  useEffect(() => {
    loadProjects(currentPage);
  }, [currentPage]);

  // Team owner: fetch member list separately (not derived from project data)
  useEffect(() => {
    if (!isTeamOwner || !user?.id) return;
    supabase
      .from('team_members')
      .select('member_user_id, user_profiles!team_members_member_user_id_fkey(first_name, last_name)')
      .eq('team_owner_id', user.id)
      .eq('is_active', true)
      .then(({ data }) => {
        if (data) {
          setTeamMembersList(data.map(row => ({
            userId: row.member_user_id,
            name: `${row.user_profiles?.first_name || ''} ${row.user_profiles?.last_name || ''}`.trim() || 'Member',
          })));
        }
      });
  }, [isTeamOwner, user?.id]);

  // Real-time subscription for project updates
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToProjectUpdates(user?.id, {
      onInsert: () => {
        // Reload page 1 to show new project at top, and update totalCount
        setCurrentPage(1);
        loadProjects(1);
      },
      onUpdate: (updatedProject) => {
        // Update in-place on current page, preserving designsCount
        setProjects((prev) =>
          prev?.map((p) => (p?.id === updatedProject?.id ? { ...updatedProject, designsCount: p?.designsCount } : p))
        );
      },
      onDelete: () => {
        // Reload current page (or previous if now empty)
        loadProjects(currentPage);
      },
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [user, selectedToolType, selectedStatus, searchQuery]);

  const loadProjects = async (page = currentPage) => {
    setIsLoading(true);

    if (!user) {
      setProjects([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    try {
      const filters = {};
      if (selectedToolType) filters.toolType = selectedToolType;
      if (selectedStatus)   filters.status   = selectedStatus;
      if (searchQuery)      filters.search   = searchQuery;

      let data = [];
      let count = 0;

      if (isTeamOwner) {
        if (selectedMember) filters.userId = selectedMember;
        const result = await projectsService?.getTeamProjects(filters, page, PROJECTS_PER_PAGE);
        data  = result.data;
        count = result.count;
      } else {
        const result = await projectsService?.getProjects(user?.id, filters, page, PROJECTS_PER_PAGE);
        data  = result.data;
        count = result.count;
      }

      // Batch design counts — 1 query for all projects on this page
      if (data.length > 0) {
        const countsMap = await projectsService?.getProjectsDesignCounts(data.map(p => p.id));
        data = data.map(p => ({ ...p, designsCount: countsMap[p.id] ?? 0 }));
      }

      setProjects(data);
      setTotalCount(count);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (user) loadProjects();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleProjectOpen = (projectId) => {
    const project = projects?.find(p => p?.id === projectId);
    if (!project) return;
    // Route by toolName first (unique per tool), fallback to toolType
    const route = toolNameRoutes?.[project?.toolName] || toolRoutes?.[project?.toolType];
    if (route) {
      navigate(route, { state: { projectId } });
    }
  };

  const handleProjectDownload = async (projectId) => {
    const project = projects?.find(p => p?.id === projectId);
    if (project?.thumbnail) {
      const link = document.createElement('a');
      link.href = project?.thumbnail;
      link.download = `${project?.name}.jpg`;
      link?.click();
    }
  };

  const handleProjectShare = (projectId) => {
    const project = projects?.find(p => p?.id === projectId);
    if (project) {
      if (navigator.share) {
        navigator.share({
          title: project?.name,
          text: project?.description || 'Check out my interior design project!',
          url: window.location?.href
        });
      } else {
        navigator.clipboard?.writeText(window.location?.href);
        alert('Link copied to clipboard!');
      }
    }
  };

  const handleProjectRename = async (projectId, newName) => {
    try {
      await projectsService?.updateProject(projectId, { name: newName });
      setProjects(projects?.map(p => p?.id === projectId ? { ...p, name: newName } : p));
    } catch (error) {
      console.error('Error renaming project:', error);
    }
  };

  const handleProjectDelete = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await projectsService?.deleteProject(projectId);
      const newCount = totalCount - 1;
      const newTotalPages = Math.max(1, Math.ceil(newCount / PROJECTS_PER_PAGE));
      const safePage = Math.min(currentPage, newTotalPages);
      setTotalCount(newCount);
      if (safePage !== currentPage) {
        setCurrentPage(safePage);
      } else {
        loadProjects(safePage);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  const handleProjectSelect = (projectId, isSelected) => {
    if (isSelected) {
      setSelectedProjects([...selectedProjects, projectId]);
    } else {
      setSelectedProjects(selectedProjects?.filter(id => id !== projectId));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjects?.length === 0) return;
    
    if (!confirm(`Are you sure you want to delete ${selectedProjects?.length} project(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      await Promise.all(selectedProjects?.map(id => projectsService?.deleteProject(id)));
      setSelectedProjects([]);
      const newCount = Math.max(0, totalCount - selectedProjects.length);
      const newTotalPages = Math.max(1, Math.ceil(newCount / PROJECTS_PER_PAGE));
      const safePage = Math.min(currentPage, newTotalPages);
      setTotalCount(newCount);
      setCurrentPage(safePage);
      loadProjects(safePage);
    } catch (error) {
      console.error('Error deleting projects:', error);
      alert('Failed to delete some projects. Please try again.');
    }
  };

  const handleImageClick = (imageIndex) => {
    setSelectedImageModal({
      index: imageIndex,
      toolType: selectedProjectTool,
      prompt: "A hyper-realistic, ultra-spacious countryside living room that masterfully blends rustic warmth with a sophisticated palette of deep hunter and soft sage greens. The room is defined by its grand architectural scale, featuring soaring double-height ceilings with exposed, rough-hewn oak beams and wide-plank, warm herringbone wood flooring.\n\nLarge panoramic windows with thin black metal frames overlook a rolling, misty meadow, flooding the space with soft, volumetric morning light and creating a serene, cinematic atmosphere. At the room's heart, a plush, oversized hunter green velvet sofa is adorned with hand-woven wool throws and textured linen cushions in oatmeal and charcoal tones.\n\nThe walls are treated with a subtle sage green lime-wash finish, providing a soft, organic texture that contrasts against a massive, floor-to-ceiling stone fireplace made of locally foraged fieldstone. The mantle is decorated with a curated selection of antique brass candleholders, a stone jug of fresh hedgerow flowers, and a weathered leather trunk serving as a coffee table.\n\nThroughout the space, an abundance of greenery, including towering potted palms and trailing ivy, creates a lush indoor oasis that blurs the line between the interior and the rural landscape outside. The lighting is layered, combining the natural daylight with the warm, amber glow of dimmable recessed lights and a singular, large wrought-iron chandelier that casts intricate shadows across the room. Every detail, from the fine grain of the wood to the individual fibers of the velvet, is rendered with meticulous, high-end architectural photography quality.",
      usedMedia: [
        "/assets/images/colored-pencil-hand-rendering-1769759929106.jpg",
        "/assets/images/tmp6t906c04-1768208158635.jpg"
      ]
    });
  };

  const closeModal = () => {
    setSelectedImageModal(null);
  };

  // Server-side filtered — no client-side member filter needed
  const displayProjects = projects;

  const isStorageFull = maxStorage !== null && storageCount >= maxStorage;

  const handleUploadMedia = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    if (isStorageFull) return;
    addUploadedMedia(files).then(() => {
      // Update storage count after upload
      setStorageCount((prev) => prev + files.length);
    });
    if (uploadInputRef.current) uploadInputRef.current.value = '';
  };

  const handleDeleteMedia = (type, index) => {
    // index here is the actual index in uploadedMedia array
    if (deleteConfirmIdx === index) {
      removeUploadedMedia(index);
      setStorageCount((prev) => Math.max(0, prev - 1));
      setDeleteConfirmIdx(null);
    } else {
      setDeleteConfirmIdx(index);
    }
  };

  return (
    <>
      <Helmet>
        <title>Project History - Roomwork.ai</title>
        <meta name="description" content="Manage and organize your AI-generated interior design projects" />
      </Helmet>
      <div className="min-h-screen bg-black">
        <Header 
          remainingCredits={userProfile?.remainingCredits || 0} 
          monthlyLimit={userProfile?.monthlyCredits || 100} 
          showCredits={true} 
        />

        <main className="container mx-auto px-4 pt-24 pb-12 lg:pt-32">
          <div className="mb-6">
            <button
              onClick={() => navigate('/main-dashboard')}
              className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-white">Project history</h1>
              <button
                onClick={() => {
                  if (maxProjects !== null && totalCount >= maxProjects) {
                    alert(`Project limit reached (${totalCount}/${maxProjects}). Please upgrade your plan or delete existing projects.`);
                    return;
                  }
                  setShowCreateModal(true);
                }}
                className="glassmorphic-button rounded-2xl px-6 py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
              >
                Create new project
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Search Section (FIRST THIRD) */}
            <div className="lg:col-span-1">
              <div className="glassmorphic-card rounded-[24px] p-6 border border-[rgba(254,131,56,0.2)]">
                {/* Project Count */}
                <div className="mb-6">
                  <p className="text-white text-base font-medium">
                    {totalCount} {totalCount === 1 ? 'project' : 'projects'}
                    {maxProjects !== null && !selectedMember && (
                      <span className="text-white/50 text-sm font-normal"> / {maxProjects} max</span>
                    )}
                  </p>
                  {isTeamOwner && (
                    <p className="text-white/40 text-xs mt-0.5">Team view — showing all members</p>
                  )}
                </div>

                {/* Search Projects Section */}
                <div className="space-y-4 mb-8">
                  <h2 className="text-white font-semibold text-lg">Search Projects</h2>

                  {/* Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e?.target?.value); setCurrentPage(1); }}
                      placeholder="Search by name / prompt keyword"
                      className="w-full rounded-[20px] px-4 py-3.5 text-white placeholder:text-white/40 text-sm bg-white/5 border border-[rgba(254,131,56,0.15)] focus:border-[rgba(254,131,56,0.3)] focus:outline-none transition-colors backdrop-blur-xl"
                    />
                  </div>

                  {/* Tool Type Dropdown — Custom */}
                  <div className="relative" ref={toolTypeRef}>
                    <button
                      onClick={() => setIsToolTypeOpen(!isToolTypeOpen)}
                      className="w-full rounded-[20px] px-4 py-3.5 text-sm bg-white/5 border border-[rgba(254,131,56,0.15)] hover:border-[rgba(254,131,56,0.3)] focus:border-[rgba(254,131,56,0.3)] focus:outline-none transition-colors backdrop-blur-xl cursor-pointer flex items-center justify-between"
                    >
                      <span className="flex items-center gap-2.5">
                        {selectedToolType ? (
                          <>
                            <Icon name={toolTypes.find(t => t.value === selectedToolType)?.icon || 'Wand2'} size={16} className="text-orange-400" />
                            <span className="text-white">{toolTypes.find(t => t.value === selectedToolType)?.label}</span>
                          </>
                        ) : (
                          <>
                            <Icon name="LayoutGrid" size={16} className="text-white/40" />
                            <span className="text-white/40">All Tool Types</span>
                          </>
                        )}
                      </span>
                      <Icon name={isToolTypeOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/40" />
                    </button>

                    {isToolTypeOpen && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-2 rounded-[20px] border border-[rgba(254,131,56,0.2)] overflow-hidden backdrop-blur-2xl"
                        style={{ background: 'linear-gradient(180deg, rgba(20,20,20,0.97) 0%, rgba(10,10,10,0.98) 100%)' }}
                      >
                        <div className="max-h-[320px] overflow-y-auto py-1.5 custom-scrollbar">
                          {/* All Tool Types option */}
                          <button
                            onClick={() => { setSelectedToolType(''); setIsToolTypeOpen(false); setCurrentPage(1); }}
                            className={`w-full px-4 py-3 flex items-center gap-3 text-sm transition-all hover:bg-white/[0.06] ${
                              !selectedToolType ? 'text-orange-400 bg-orange-500/[0.08]' : 'text-white/70'
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              !selectedToolType ? 'bg-orange-500/20' : 'bg-white/[0.06]'
                            }`}>
                              <Icon name="LayoutGrid" size={14} className={!selectedToolType ? 'text-orange-400' : 'text-white/50'} />
                            </div>
                            <span className="font-medium">All Tool Types</span>
                            {!selectedToolType && (
                              <Icon name="Check" size={14} className="text-orange-400 ml-auto" />
                            )}
                          </button>

                          <div className="mx-4 border-t border-white/[0.06]" />

                          {toolTypes.map((type) => (
                            <button
                              key={type.value}
                              onClick={() => { setSelectedToolType(type.value); setIsToolTypeOpen(false); setCurrentPage(1); }}
                              className={`w-full px-4 py-3 flex items-center gap-3 text-sm transition-all hover:bg-white/[0.06] ${
                                selectedToolType === type.value ? 'text-orange-400 bg-orange-500/[0.08]' : 'text-white/70'
                              }`}
                            >
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                selectedToolType === type.value ? 'bg-orange-500/20' : 'bg-white/[0.06]'
                              }`}>
                                <Icon name={type.icon} size={14} className={selectedToolType === type.value ? 'text-orange-400' : 'text-white/50'} />
                              </div>
                              <span className="font-medium">{type.label}</span>
                              {selectedToolType === type.value && (
                                <Icon name="Check" size={14} className="text-orange-400 ml-auto" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Member Filter (team owner only) */}
                  {isTeamOwner && teamMembersList.length > 0 && (
                    <div>
                      <select
                        value={selectedMember}
                        onChange={(e) => { setSelectedMember(e.target.value); setCurrentPage(1); }}
                        className="w-full rounded-[20px] px-4 py-3.5 text-sm bg-white/5 border border-[rgba(254,131,56,0.15)] hover:border-[rgba(254,131,56,0.3)] focus:border-[rgba(254,131,56,0.3)] focus:outline-none transition-colors backdrop-blur-xl cursor-pointer text-white appearance-none"
                      >
                        <option value="" className="bg-[#111] text-white">All Members</option>
                        <option value={user?.id} className="bg-[#111] text-white">You (Owner)</option>
                        {teamMembersList.map((m) => (
                          <option key={m.userId} value={m.userId} className="bg-[#111] text-white">
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Date Field with Calendar */}
                  <div className="relative">
                    <button
                      onClick={() => setShowCalendar(!showCalendar)}
                      className="w-full rounded-[20px] px-4 py-3.5 text-white text-sm bg-white/5 border border-[rgba(254,131,56,0.15)] focus:border-[rgba(254,131,56,0.3)] transition-colors backdrop-blur-xl text-left flex items-center justify-between"
                    >
                      <span>
                        {selectedDate
                          ? `Date: ${selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                          : 'Select date'}
                      </span>
                      <Icon name="Calendar" size={16} className="text-white/50" />
                    </button>
                    <CalendarPopup
                      isOpen={showCalendar}
                      onClose={() => setShowCalendar(false)}
                      selectedDate={selectedDate}
                      onDateSelect={(d) => { setSelectedDate(d); setCurrentPage(1); }}
                      generationDates={localProjects.flatMap((p) => p.images.map((img) => img.savedAt))}
                    />
                  </div>

                  {/* Search Button */}
                  <button
                    onClick={loadProjects}
                    className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
                  >
                    Search
                  </button>
                </div>

                {/* Storage Section */}
                <div className="space-y-4">
                  <h2 className="text-white font-semibold text-lg">Storage</h2>

                  {/* Storage Toggle Buttons */}
                  <div className="space-y-3">
                    {/* Uploaded Media Button */}
                    <button
                      onClick={() => setActiveStorageTab(activeStorageTab === 'uploaded' ? null : 'uploaded')}
                      className={`w-full rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 transition-all bg-white/5 border border-white/10 hover:border-white/20 ${
                        activeStorageTab === 'uploaded' ? 'scale-[1.02] border-white/20' : 'hover:scale-[1.02]'
                      }`}
                    >
                      Uploaded Media
                    </button>

                    {/* Uploaded Media Content */}
                    {activeStorageTab === 'uploaded' && (() => {
                      const MEDIA_PER_PAGE = 10;
                      const mediaTotalPages = Math.ceil((uploadedMedia?.length || 0) / MEDIA_PER_PAGE);
                      const mediaStart = (mediaPage - 1) * MEDIA_PER_PAGE;
                      const pagedMedia = (uploadedMedia || []).slice(mediaStart, mediaStart + MEDIA_PER_PAGE);

                      return (
                        <div className="glassmorphic-card rounded-[20px] p-4 border border-[rgba(254,131,56,0.15)]">
                          {uploadedMedia?.length > 0 && (
                            <>
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                {pagedMedia.map((img, idx) => {
                                  const actualIdx = mediaStart + idx;
                                  return (
                                    <div key={actualIdx} className="relative group">
                                      <img
                                        src={img}
                                        alt={`Uploaded media ${actualIdx + 1}`}
                                        className="w-full aspect-square object-contain rounded-xl border border-white/10 bg-black/30"
                                      />
                                      <button
                                        onClick={() => handleDeleteMedia('uploaded', actualIdx)}
                                        className={`absolute top-2 right-2 rounded-lg p-1.5 transition-all ${
                                          deleteConfirmIdx === actualIdx
                                            ? 'bg-red-600 opacity-100'
                                            : 'bg-black/70 hover:bg-red-500/70 opacity-0 group-hover:opacity-100'
                                        }`}
                                      >
                                        <Icon name="Trash2" size={14} className="text-white" />
                                      </button>
                                      {deleteConfirmIdx === actualIdx && (
                                        <div className="absolute inset-x-0 bottom-0 bg-black/80 rounded-b-xl px-2 py-1.5 flex items-center justify-between">
                                          <span className="text-red-400 text-[10px] font-medium">Delete this image?</span>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmIdx(null); }}
                                            className="text-white/50 text-[10px] hover:text-white"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Pagination */}
                              {mediaTotalPages > 1 && (
                                <div className="flex items-center justify-center gap-1.5 mb-3">
                                  <button
                                    onClick={() => setMediaPage((p) => Math.max(1, p - 1))}
                                    disabled={mediaPage === 1}
                                    className="px-2 py-1 rounded-lg text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                  >
                                    Prev
                                  </button>
                                  {Array.from({ length: mediaTotalPages }, (_, i) => i + 1).map((p) => (
                                    <button
                                      key={p}
                                      onClick={() => setMediaPage(p)}
                                      className={`min-w-[28px] py-1 rounded-lg text-xs font-medium transition-all ${
                                        mediaPage === p
                                          ? 'bg-orange-500 text-white'
                                          : 'text-white/60 hover:text-white border border-white/10 hover:border-white/20'
                                      }`}
                                    >
                                      {p}
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => setMediaPage((p) => Math.min(mediaTotalPages, p + 1))}
                                    disabled={mediaPage === mediaTotalPages}
                                    className="px-2 py-1 rounded-lg text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                  >
                                    Next
                                  </button>
                                </div>
                              )}

                              <p className="text-white/30 text-xs text-center mb-3">
                                {mediaStart + 1}–{Math.min(mediaStart + MEDIA_PER_PAGE, uploadedMedia.length)} of {uploadedMedia.length} images
                              </p>
                            </>
                          )}
                          {uploadedMedia?.length === 0 && (
                            <p className="text-white/30 text-xs text-center mb-3">No images uploaded yet</p>
                          )}
                          <input
                            ref={uploadInputRef}
                            type="file"
                            multiple
                            accept="image/png,image/jpeg,image/jpg"
                            className="hidden"
                            onChange={handleUploadMedia}
                          />
                          <button
                            onClick={() => !isStorageFull && uploadInputRef.current?.click()}
                            disabled={uploadingMedia || isStorageFull}
                            className="w-full flex items-center justify-center gap-2 rounded-xl py-2.5 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              background: isStorageFull
                                ? 'rgba(255, 255, 255, 0.03)'
                                : 'linear-gradient(135deg, rgba(254, 131, 56, 0.15) 0%, rgba(236, 103, 30, 0.08) 100%)',
                              border: isStorageFull
                                ? '1px solid rgba(255, 255, 255, 0.08)'
                                : '1px solid rgba(254, 131, 56, 0.25)',
                              boxShadow: isStorageFull ? 'none' : '0 0 20px rgba(254, 131, 56, 0.08)',
                            }}
                          >
                            {uploadingMedia ? (
                              <>
                                <Icon name="Loader2" size={18} className="text-orange-500 animate-spin" />
                                <span className="text-white text-sm font-medium">Uploading...</span>
                              </>
                            ) : isStorageFull ? (
                              <>
                                <Icon name="Lock" size={18} className="text-white/30" />
                                <span className="text-white/30 text-sm font-medium">Storage full ({storageCount}/{maxStorage})</span>
                              </>
                            ) : (
                              <>
                                <Icon name="Plus" size={18} className="text-orange-500" />
                                <span className="text-white text-sm font-medium">Upload</span>
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })()}

                    {/* Buy Storage Button */}
                    <button
                      onClick={() => navigate('/subscription-payment')}
                      className="w-full rounded-2xl py-2.5 text-white text-xs font-medium hover:scale-[1.02] transition-transform bg-white/5 border border-white/10 hover:border-white/20"
                    >
                      Buy Storage
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Projects Grid */}
            <div className="lg:col-span-2 pb-[20vh]">
              {selectedProjects?.length > 0 && (
                <div className="mb-4 glassmorphic-card rounded-[20px] p-4 border border-[rgba(254,131,56,0.2)] flex items-center justify-between">
                  <p className="text-white text-sm">
                    {selectedProjects?.length} project(s) selected
                  </p>
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-destructive/20 text-destructive rounded-lg hover:bg-destructive/30 transition-colors text-sm"
                  >
                    Delete Selected
                  </button>
                </div>
              )}

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-white/70">Loading projects...</div>
                </div>
              ) : displayProjects?.length === 0 && localProjects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Icon name="FolderOpen" size={48} className="text-white/20 mb-4" />
                  <p className="text-white/50 text-lg">No projects yet</p>
                  <p className="text-white/30 text-sm mt-1">Create a new project to get started</p>
                </div>
              ) : (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Local Projects */}
                  {localProjects.map((proj) => (
                    <div
                      key={`local-${proj.id}`}
                      className="glassmorphic-card rounded-2xl p-5 transition-all duration-300"
                      style={{ border: '1px solid rgba(255, 255, 255, 0.06)' }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        {renamingProjectId === proj.id ? (
                          <input
                            type="text"
                            value={renamingValue}
                            onChange={(e) => setRenamingValue(e.target.value)}
                            onBlur={() => {
                              if (renamingValue.trim()) renameProject(proj.id, renamingValue.trim());
                              setRenamingProjectId(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                if (renamingValue.trim()) renameProject(proj.id, renamingValue.trim());
                                setRenamingProjectId(null);
                              }
                            }}
                            className="text-lg font-bold text-white bg-white/[0.05] border border-[rgba(254,131,56,0.3)] rounded-lg px-3 py-1 flex-1 mr-3 focus:outline-none focus:border-[rgba(254,131,56,0.5)] focus:shadow-[0_0_12px_rgba(254,131,56,0.15)]"
                            autoFocus
                          />
                        ) : (
                          <h3 className="text-lg font-bold text-white truncate flex-1 mr-3">{proj.name}</h3>
                        )}
                        <button
                          onClick={() => { setRenamingProjectId(proj.id); setRenamingValue(proj.name); }}
                          className="text-white/30 hover:text-orange-400 transition-colors flex-shrink-0"
                        >
                          <Icon name="Pencil" size={18} />
                        </button>
                      </div>

                      <p className="text-xs text-white/50 font-medium mb-2">Used tools:</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {proj.usedTools.length > 0 ? (
                          proj.usedTools.map((tool) => (
                            <span key={tool} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/80" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>{tool}</span>
                          ))
                        ) : (
                          <span className="text-xs text-white/30">No tools used yet</span>
                        )}
                      </div>

                      <div className="mb-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />

                      <button
                        onClick={() => setExpandedProject(expandedProject === proj.id ? null : proj.id)}
                        className="flex items-center gap-2 text-sm text-white/50 hover:text-white/70 transition-colors mb-3"
                      >
                        <Icon name={expandedProject === proj.id ? "ChevronDown" : "ChevronRight"} size={16} />
                        <span>{proj.images.length} generated images</span>
                      </button>

                      {expandedProject === proj.id && proj.images.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {proj.images.map((img, idx) => (
                            <div key={idx} onClick={() => setImagePreviewModal({ url: img.url, tool: img.tool })} className="aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10 hover:border-orange-500/30 transition-all cursor-pointer">
                              <img src={img.url} alt={`Generated by ${img.tool}`} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={() => { if (confirm('Are you sure you want to delete this project?')) deleteLocalProject(proj.id); }}
                          className="flex items-center gap-2 text-xs text-white/30 hover:text-red-400 transition-colors"
                        >
                          <Icon name="Trash2" size={14} />
                          <span>Delete project</span>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* DB Projects */}
                  {displayProjects?.map((project) => {
                    const isOwnProject = project?.userId === user?.id;
                    const memberName = isOwnProject
                      ? 'You'
                      : `${project?.memberFirstName || ''} ${project?.memberLastName || ''}`.trim() || 'Member';
                    return (
                      <ProjectCard
                        key={project?.id}
                        project={{
                          id: project?.id,
                          name: project?.name,
                          thumbnail: project?.thumbnail || '/assets/images/no_image.png',
                          thumbnailAlt: `${project?.name} thumbnail`,
                          toolType: project?.toolType,
                          toolName: project?.toolName,
                          createdAt: project?.createdAt,
                          variations: project?.variations,
                          designsCount: project?.designsCount,
                          tags: project?.tags || [],
                          status: project?.status,
                          isOwnProject,
                        }}
                        memberName={isTeamOwner ? memberName : null}
                        isOwnerView={isTeamOwner}
                        onOpen={handleProjectOpen}
                        onRename={isOwnProject ? handleProjectRename : undefined}
                        onDownload={handleProjectDownload}
                        onShare={handleProjectShare}
                        onDelete={isOwnProject ? handleProjectDelete : undefined}
                        isSelected={selectedProjects?.includes(project?.id)}
                        onSelect={handleProjectSelect}
                      />
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalCount > PROJECTS_PER_PAGE && (() => {
                  const totalPages = Math.ceil(totalCount / PROJECTS_PER_PAGE);
                  const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  const endPage = Math.min(totalPages, startPage + 4);
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) pages.push(i);

                  return (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 px-1">
                      <span className="text-sm text-white/40">
                        {(currentPage - 1) * PROJECTS_PER_PAGE + 1}–{Math.min(currentPage * PROJECTS_PER_PAGE, totalCount)} of {totalCount} projects
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1.5 rounded-xl text-sm font-medium transition-all border border-white/10 hover:border-orange-500/30 hover:bg-white/5 text-white/60 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          Prev
                        </button>
                        {pages.map((p) => (
                          <button
                            key={p}
                            onClick={() => setCurrentPage(p)}
                            className={`min-w-[36px] py-1.5 rounded-xl text-sm font-medium transition-all ${currentPage === p ? 'bg-orange-500/20 border border-orange-500/40 text-orange-400' : 'border border-white/10 hover:border-orange-500/30 hover:bg-white/5 text-white/60'}`}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1.5 rounded-xl text-sm font-medium transition-all border border-white/10 hover:border-orange-500/30 hover:bg-white/5 text-white/60 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            const num = parseInt(pageInput, 10);
                            if (num >= 1 && num <= totalPages) { setCurrentPage(num); }
                            setPageInput('');
                          }}
                          className="flex items-center gap-1.5 ml-3"
                        >
                          <input
                            type="text"
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))}
                            placeholder="Go to"
                            className="w-16 px-2 py-1.5 rounded-xl text-sm border border-white/10 bg-white/5 text-white text-center focus:outline-none focus:border-orange-500/40"
                          />
                          <button
                            type="submit"
                            className="px-2.5 py-1.5 rounded-xl text-sm font-medium border border-white/10 hover:border-orange-500/30 hover:bg-white/5 text-white/60 transition-all"
                          >
                            Go
                          </button>
                        </form>
                      </div>
                    </div>
                  );
                })()}
                </>
              )}
            </div>
          </div>
        </main>

        {/* Image Detail Modal */}
        {selectedImageModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Dimmed Overlay */}
            <div 
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={closeModal}
            ></div>

            {/* Modal Content */}
            <div className="relative w-[90%] md:w-[50%] max-h-[80vh] glassmorphic-card rounded-[24px] border border-[rgba(254,131,56,0.3)] p-6 md:p-8 overflow-hidden flex flex-col">
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10"
              >
                <Icon name="X" size={24} />
              </button>

              {/* Scrollable Content with more spacing */}
              <div className="overflow-y-auto flex-1 pr-4 mt-12 custom-scrollbar">
                {/* Prompt Section */}
                <div className="mb-6">
                  <h3 className="text-white font-semibold text-lg mb-3">Prompt:</h3>
                  <p className="text-white/80 text-sm leading-relaxed whitespace-pre-line">
                    {selectedImageModal?.prompt}
                  </p>
                </div>

                {/* Used Media Section */}
                <div>
                  <h3 className="text-white font-semibold text-lg mb-3">Used media:</h3>
                  <div className="flex gap-4">
                    {selectedImageModal?.usedMedia?.map((mediaUrl, idx) => (
                      <div
                        key={idx}
                        className="w-32 h-32 rounded-xl overflow-hidden bg-white/5 border border-white/10"
                      >
                        <img
                          src={mediaUrl}
                          alt={`Used media ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Fullscreen Image Preview Modal */}
        {imagePreviewModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => { setImagePreviewModal(null); setShowContinueEditingDropdown(false); }}
            />
            <div className="relative w-[90%] max-w-lg glassmorphic-card rounded-[24px] border border-[rgba(254,131,56,0.3)] p-6">
              <button
                onClick={() => { setImagePreviewModal(null); setShowContinueEditingDropdown(false); }}
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10"
              >
                <Icon name="X" size={24} />
              </button>

              <h2 className="text-white text-lg font-semibold mb-1">Generated image</h2>
              {imagePreviewModal.tool && (
                <p className="text-white/50 text-sm mb-4">
                  Generated with <span className="text-orange-500">{imagePreviewModal.tool}</span>
                </p>
              )}

              <div className="rounded-xl overflow-hidden mb-5 border border-white/10">
                <img
                  src={imagePreviewModal.url}
                  alt="Generated image preview"
                  className="w-full object-contain max-h-[50vh]"
                />
              </div>

              {/* Continue Editing */}
              <div className="relative mb-4">
                <button
                  onClick={() => setShowContinueEditingDropdown(!showContinueEditingDropdown)}
                  className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
                >
                  Continue editing
                  <Icon name={showContinueEditingDropdown ? "ChevronUp" : "ChevronDown"} size={16} />
                </button>
                {showContinueEditingDropdown && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#1a1a1a] rounded-2xl border border-white/10 p-2 max-h-[240px] overflow-y-auto custom-scrollbar z-20">
                    {allTools.map((tool) => (
                      <button
                        key={tool.route}
                        onClick={() => {
                          setImagePreviewModal(null);
                          setShowContinueEditingDropdown(false);
                          navigate(tool.route);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                      >
                        {tool.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Download */}
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = imagePreviewModal.url;
                  link.download = 'generated-image.jpg';
                  link.click();
                }}
                className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-white transition-colors text-sm py-2"
              >
                <Icon name="Download" size={16} />
                <span>Download image</span>
              </button>
            </div>
          </div>
        )}

        {/* Create Project Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => { setShowCreateModal(false); setNewProjectName(''); setNewProjectTool(''); setIsCreateToolOpen(false); }}
            />
            <div className="relative w-[90%] max-w-md glassmorphic-card rounded-[24px] border border-[rgba(254,131,56,0.3)] p-6">
              <button
                onClick={() => { setShowCreateModal(false); setNewProjectName(''); setNewProjectTool(''); setIsCreateToolOpen(false); }}
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
              <h2 className="text-white text-xl font-bold mb-4">Create new project</h2>
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newProjectName.trim() && newProjectTool) {
                    handleCreateProject();
                  }
                }}
                placeholder="Project name (e.g. Green Living Room)"
                className="w-full rounded-2xl px-4 py-3.5 text-white placeholder:text-white/40 text-sm bg-white/5 border border-[rgba(254,131,56,0.15)] focus:border-[rgba(254,131,56,0.3)] focus:outline-none transition-colors mb-4"
                autoFocus
              />
              <div className="relative mb-4" ref={createToolRef}>
                <button
                  type="button"
                  onClick={() => setIsCreateToolOpen(!isCreateToolOpen)}
                  className="w-full rounded-[20px] px-4 py-3.5 text-sm bg-white/5 border border-[rgba(254,131,56,0.15)] hover:border-[rgba(254,131,56,0.3)] focus:border-[rgba(254,131,56,0.3)] focus:outline-none transition-colors backdrop-blur-xl cursor-pointer flex items-center justify-between"
                >
                  <span className="flex items-center gap-2.5">
                    {newProjectTool ? (
                      <>
                        <Icon name={toolTypes.find(t => t.value === newProjectTool)?.icon || 'Wand2'} size={16} className="text-orange-400" />
                        <span className="text-white">{toolTypes.find(t => t.value === newProjectTool)?.label}</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Wand2" size={16} className="text-white/40" />
                        <span className="text-white/40">Select a tool...</span>
                      </>
                    )}
                  </span>
                  <Icon name={isCreateToolOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/40" />
                </button>

                {isCreateToolOpen && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-2 rounded-[20px] border border-[rgba(254,131,56,0.2)] overflow-hidden backdrop-blur-2xl"
                    style={{ background: 'linear-gradient(180deg, rgba(20,20,20,0.97) 0%, rgba(10,10,10,0.98) 100%)' }}
                  >
                    <div className="max-h-[280px] overflow-y-auto py-1.5 custom-scrollbar">
                      {toolTypes.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => { setNewProjectTool(type.value); setIsCreateToolOpen(false); }}
                          className={`w-full px-4 py-3 flex items-center gap-3 text-sm transition-all hover:bg-white/[0.06] ${
                            newProjectTool === type.value ? 'text-orange-400 bg-orange-500/[0.08]' : 'text-white/70'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            newProjectTool === type.value ? 'bg-orange-500/20' : 'bg-white/[0.06]'
                          }`}>
                            <Icon name={type.icon} size={14} className={newProjectTool === type.value ? 'text-orange-400' : 'text-white/50'} />
                          </div>
                          <span className="font-medium">{type.label}</span>
                          {newProjectTool === type.value && (
                            <Icon name="Check" size={14} className="text-orange-400 ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim() || !newProjectTool || isCreating}
                className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create project'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectHistory;